<script lang="ts">
  import { onMount } from 'svelte'
  import { intake } from '$lib/public/intake-client'
  import { showError } from '$lib/util/errors'
  import type { Draft } from '$lib/public/wizard-state'
  import type { paths } from '$lib/api/generated'

  type PublicUnit =
    paths['/public/units']['get']['responses'][200]['content']['application/json']['items'][number]

  interface ApiError {
    detail?: string
  }

  interface Props {
    unitId: Draft['unitId']
    propertyId?: number | undefined
  }
  let { unitId = $bindable(), propertyId }: Props = $props()

  let unitOptions = $state<PublicUnit[]>([])
  let loading = $state(true)

  onMount(async () => {
    const query: { availableOnly: boolean; propertyId?: number } = { availableOnly: true }
    if (propertyId) query.propertyId = propertyId
    const { data, error } = await intake.GET('/public/units', {
      params: { query },
    })
    if (error) {
      showError(error as ApiError)
      loading = false
      return
    }
    unitOptions = data?.items ?? []
    loading = false
  })

  const selected = $derived(unitOptions.find((u) => u.id === unitId) ?? null)
</script>

<h2 tabindex="-1">Which unit are you interested in?</h2>
<p class="muted">Step three of three. Available units only - occupied ones aren't shown.</p>

{#if loading}
  <p>Loading available units…</p>
{:else if unitOptions.length === 0}
  <p class="empty">No units are currently available. Please check back later.</p>
{:else}
  <div class="form-field">
    <label for="unit-select">Unit</label>
    <select id="unit-select" bind:value={unitId}>
      <option value={null}>- Select a unit -</option>
      {#each unitOptions as unit (unit.id)}
        <option value={unit.id}>
          {unit.streetAddress}{unit.unitNumber ? ` #${unit.unitNumber}` : ''}, {unit.city}, {unit.state}
        </option>
      {/each}
    </select>
  </div>

  {#if selected}
    <div class="unit-details">
      <h3>{selected.streetAddress}{selected.unitNumber ? ` #${selected.unitNumber}` : ''}</h3>
      <p class="location">{selected.city}, {selected.state}</p>
      <ul>
        {#if selected.bedrooms !== null && selected.bedrooms !== undefined}
          <li><strong>Bedrooms:</strong> {selected.bedrooms}</li>
        {/if}
        {#if selected.bathrooms !== null && selected.bathrooms !== undefined}
          <li><strong>Bathrooms:</strong> {selected.bathrooms}</li>
        {/if}
        <li><strong>Property manager:</strong> {selected.propertyManagerName}</li>
      </ul>
    </div>
  {/if}
{/if}

<style>
  h2 {
    margin: 0 0 4px;
    font-size: 1.4rem;
    color: var(--ink-strong);
  }
  h2:focus {
    outline: none;
  }
  .muted {
    margin: 0 0 20px;
    color: var(--muted);
  }
  .empty {
    color: var(--muted);
    font-style: italic;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .form-field select {
    padding: 10px 12px;
    min-height: 44px; /* comfortable touch target */
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 1rem;
  }
  .unit-details {
    background: var(--surface-2);
    padding: 16px;
    border-radius: 8px;
    border: 1px solid var(--line);
  }
  .unit-details h3 {
    margin: 0 0 4px;
    font-size: 1.1rem;
  }
  .unit-details .location {
    margin: 0 0 12px;
    color: var(--muted);
    font-size: 0.9rem;
  }
  .unit-details ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .unit-details li {
    padding: 4px 0;
    font-size: 0.9rem;
    color: var(--ink);
  }
</style>
